// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { UnitOfMeasure } from '@/constants/unit-of-measure';
import { itemSchema } from '@/items/api/types';
import type { ItemType } from '@/items/constants/item-types';
import { z } from 'zod';

/**
 * Schema for the create item API response.
 */
const createItemResponseSchema = z.object({
  data: itemSchema,
  metadata: responseMetadataSchema,
});

/**
 * Common parameters for creating any item type.
 */
interface BaseCreateItemParams {
  sku: string;
  name: string;
  description: string | null;
}

/**
 * Parameters for creating a SERVICE or DEVICE item (products).
 */
interface CreateProductItemParams extends BaseCreateItemParams {
  type: 'SERVICE' | 'DEVICE';
}

/**
 * Parameters for creating a BULK item (part).
 */
interface CreateBulkItemParams extends BaseCreateItemParams {
  type: 'BULK';
  unitOfMeasure: UnitOfMeasure;
  acceptsBulkSerials: boolean;
}

/**
 * Union of all create item parameter types.
 */
export type CreateItemParams = CreateProductItemParams | CreateBulkItemParams;

/**
 * Result of creating an item.
 * Contains the key and type of the created item.
 */
interface CreateItemResult {
  key: string;
  sku: string;
  name: string;
  itemType: ItemType;
}

/**
 * Creates an item via the unified item API endpoint.
 *
 * POST /v1/catalog/item
 *
 * The backend routes to the appropriate handler based on type:
 * - SERVICE → product with isNonInventory: true
 * - DEVICE → product with isNonInventory: false
 * - BULK → part
 *
 * @param params - The parameters for creating the item
 * @returns A promise that resolves to the created item info
 */
export async function createItem(
  params: CreateItemParams
): Promise<CreateItemResult> {
  const url = '/v1/catalog/item';

  const requestBody: Record<string, unknown> = {
    type: params.type,
    sku: params.sku,
    name: params.name,
    description: params.description,
  };

  // Add BULK-specific fields
  if (params.type === 'BULK') {
    requestBody.unitOfMeasure = params.unitOfMeasure;
    requestBody.acceptsBulkSerials = params.acceptsBulkSerials;
  }

  const response = await api.post(url, requestBody);
  const parsed = await parseResponse(createItemResponseSchema, response.data, {
    schemaName: 'CreateItem',
    endpoint: url,
  });
  return {
    key: parsed.data.key,
    sku: parsed.data.sku,
    name: parsed.data.name,
    itemType: parsed.data.type,
  };
}
